import React, { useEffect, useState } from 'react';
import { Collapse } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    CheckBlueIcon as CheckRoundIcon,
    CheckIcon,
    WebsitePrimaryButton,
    TertiaryButton,
    UnfoldLessIcon,
    UnfoldMoreIcon,
    SecondaryButton,
    palette,
    TipsCard,
    Typography,
    Alert,
} from '@dev/ee-components';
import useMobile from '@/services/hooks/useMobile';
import { SaveOnboardingStart } from '@/store/onboarding/actions';
import { OnboardingStepItem } from '../../types';
import {
    OnboardingCardStepContent,
    OnboardingCardStepFeature,
    OnboardingCardStepSecondaryContent,
    OnboardingCardStepStyledTip,
    OnboardingCardStepStyledTipContent,
    OnboardingStepButtons,
    OnboardingStepHeader,
    OnboardingStepHeaderNo,
    OnboardingStepHeaderTitle,
    OnboardingStepReopen,
    OnboardingStepWrapper,
    OnboaringStepCollapseContent,
    OnboaringStepCollapseContentGrid,
} from './styles';
import { OnboardingStepLinks } from './components/OnboardingStepLinks';

export const OnboardingCardStep = ({
    no,
    title,
    isSkipped,
    isNotReady,
    isCompleted,
    isActive,
    features,
    tip,
    links,
    secondaryContent,
    primaryActionTitle,
    primaryActionCallback,
    secondaryActionTitle,
    secondaryActionCallback,
    skipSavedDataKey,
    selectedStepsIndex,
}: OnboardingStepItem): JSX.Element => {
    const { t } = useTranslation();
    const isMobile = useMobile();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const dispatch = useDispatch();

    useEffect(() => {
        !isActive && setIsOpen(false);
    }, [selectedStepsIndex]);

    useEffect(() => {
        isActive && setIsOpen(true);
    }, [isActive]);

    const handleStepClick = () => {
        if (isSkipped) {
            return;
        }
        setIsOpen((isOpen) => !isOpen);
    };

    const handleSkipStep = (): void => {
        dispatch(SaveOnboardingStart({ [skipSavedDataKey]: true }));
        setIsOpen(false);
    };

    const handleReopenStep = (): void => {
        dispatch(SaveOnboardingStart({ [skipSavedDataKey]: false }));
        setIsOpen(true);
    };

    const handlePrimaryActionClick = (): void => {
        if (isCompleted) {
            setIsOpen(false);
        }
        primaryActionCallback();
    };

    useEffect(() => {
        (isCompleted || isSkipped) && isOpen && setIsOpen(false);
    }, [isCompleted, isSkipped]);

    return (
        <OnboardingStepWrapper>
            <OnboardingStepHeader $isDisabled={isSkipped} onClick={handleStepClick} $isMobile={isMobile}>
                {isCompleted ? (
                    <CheckRoundIcon />
                ) : (
                    <OnboardingStepHeaderNo $isDisabled={isSkipped}>
                        <Typography variant="h3">{no}</Typography>
                    </OnboardingStepHeaderNo>
                )}
                <OnboardingStepHeaderTitle $isMobile={isMobile}>
                    <Typography variant="h2">{title}</Typography>
                    {isSkipped && (
                        <OnboardingStepReopen>
                            <TertiaryButton text={t('reopenthistep')} onClick={handleReopenStep} />
                        </OnboardingStepReopen>
                    )}
                </OnboardingStepHeaderTitle>

                {isOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            </OnboardingStepHeader>
            <Collapse in={isOpen}>
                <OnboaringStepCollapseContent $isMobile={isMobile}>
                    {isNotReady && <Alert severity="info" body={<div>{t('completepreviousstepstoproceed')}</div>} />}
                    <OnboaringStepCollapseContentGrid $isMobile={isMobile}>
                        <OnboardingCardStepContent>
                            {features?.map((feature,idx) => (
                                <OnboardingCardStepFeature key={`single-feature-${feature}-${idx}`}>
                                    <CheckIcon color={palette.teal[4]} width={25} height={24} />
                                    <Typography variant="body1" bodyVariant="onPaper">
                                        {feature}
                                    </Typography>
                                </OnboardingCardStepFeature>
                            ))}

                            {!!tip && (
                                <OnboardingCardStepStyledTip>
                                    <TipsCard
                                        isMobile={isMobile}
                                        cards={[
                                            {
                                                text: (
                                                    <OnboardingCardStepStyledTipContent>
                                                        {tip}
                                                    </OnboardingCardStepStyledTipContent>
                                                ),
                                            },
                                        ]}
                                    />
                                </OnboardingCardStepStyledTip>
                            )}
                            {links && <OnboardingStepLinks links={links} />}
                        </OnboardingCardStepContent>
                        <OnboardingCardStepSecondaryContent>{secondaryContent}</OnboardingCardStepSecondaryContent>
                    </OnboaringStepCollapseContentGrid>
                    <OnboardingStepButtons>
                        <WebsitePrimaryButton
                            text={primaryActionTitle}
                            onClick={handlePrimaryActionClick}
                            disabled={isSkipped || isNotReady}
                            disableSentenceCase
                        />
                        {secondaryActionTitle && (
                            <SecondaryButton
                                text={secondaryActionTitle}
                                onClick={secondaryActionCallback}
                                disabled={isSkipped || isNotReady}
                            />
                        )}
                        <TertiaryButton text={t('skipthisstep')} onClick={handleSkipStep} disabled={isCompleted} />
                    </OnboardingStepButtons>
                </OnboaringStepCollapseContent>
            </Collapse>
        </OnboardingStepWrapper>
    );
};
